import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  ChevronDownLine,
  ChevronRightLine,
  CircleSmallFilledFill,
  VideoLine,
} from '@dropbox/dig-icons/assets';
import {ButtonLink, Link} from 'components/DSYS/Link';
import {useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {addDays, format, formatDistanceToNow, startOfDay} from 'date-fns';
import {t} from 'i18next';
import {
  ComponentProps,
  KeyboardEvent,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Trans} from 'react-i18next';
import {logDashboardInteraction} from 'views/home/dashboard/dashboardAnalytics';
import {AgendaItem, DashboardState, TileId} from 'views/home/dashboard/dashboardModel';
import {useDashboardTileId} from 'views/home/dashboard/DashboardTileContext';
import {Tile} from 'views/home/dashboard/Tile';
import {TileRow} from 'views/home/dashboard/TileRow';
import {
  extractAgendaDescriptionLinks,
  formatAgendaLocation,
  hasAgendaExpandableContent,
  resolveAgendaJoinUrl,
} from 'views/home/dashboard/tiles/agendaDescriptionLinks';
import {AgendaLinkChip} from 'views/home/dashboard/tiles/AgendaLinkChip';
import {AgendaLinksMenuChip} from 'views/home/dashboard/tiles/AgendaLinksMenuChip';

import styles from './AgendaTile.module.css';

type RowKind = 'allday' | 'past' | 'highlight' | 'upcoming' | 'future';

type RowAccent = ComponentProps<typeof Box>['borderColor'];

const getRowAccent = (evt: AgendaItem, kind: RowKind): RowAccent | undefined => {
  if (evt.source === 'company') {
    return 'Identity Magenta';
  }
  if (kind === 'past') {
    return 'Border Subtle';
  }
  if (kind === 'allday') {
    return undefined;
  }
  return 'Primary Surface';
};

const UPCOMING_DAYS = 6;
const ALLDAY_GROUP_KEY = '__allday__';
const STARTING_SOON_MS = 5 * 60 * 1000;
const LONG_PERSONAL_EVENT_ALL_DAY_MS = 8 * 60 * 60 * 1000;
// Regional "focus day"-style events span nearly a full 24h but aren't flagged
// all-day and are shifted per timezone, so they cross midnight and never look
// all-day on any single calendar day. Treat an event whose *total* span is near
// a full day as all-day (regardless of source) on whichever day(s) it mostly
// covers.
const NEAR_FULL_DAY_MS = 23 * 60 * 60 * 1000;

const getAgendaItemOverlapMs = (
  evt: AgendaItem,
  rangeStart: number,
  rangeEndExclusive: number
): number => Math.max(0, Math.min(evt.end, rangeEndExclusive) - Math.max(evt.start, rangeStart));

const isNearFullDayEvent = (evt: AgendaItem): boolean => evt.end - evt.start >= NEAR_FULL_DAY_MS;

export const isAgendaItemAllDayForDay = (evt: AgendaItem, dayStart: number): boolean => {
  if (evt.allDay) {
    return true;
  }
  const overlapMs = getAgendaItemOverlapMs(evt, dayStart, addDays(dayStart, 1).getTime());
  // Near-full-day events roll up as all-day even for company events, on any day
  // they substantially cover (reusing the long-event overlap threshold).
  if (isNearFullDayEvent(evt) && overlapMs > LONG_PERSONAL_EVENT_ALL_DAY_MS) {
    return true;
  }
  if (evt.source !== 'personal') {
    return false;
  }
  return overlapMs > LONG_PERSONAL_EVENT_ALL_DAY_MS;
};

// All-day events are never "live" for countdown/join purposes.
const isAgendaItemInProgress = (evt: AgendaItem, now: number): boolean =>
  !isAgendaItemAllDayForDay(evt, startOfDay(now).getTime()) && evt.start <= now && evt.end > now;

const isAgendaItemStartingSoon = (evt: AgendaItem, now: number): boolean =>
  !isAgendaItemAllDayForDay(evt, startOfDay(now).getTime()) &&
  evt.start > now &&
  evt.start - now <= STARTING_SOON_MS;

const canJoinAgendaItem = (evt: AgendaItem, now: number): boolean =>
  evt.end > now && (isAgendaItemInProgress(evt, now) || isAgendaItemStartingSoon(evt, now));

// Half-open day overlap — multi-day events show on each day they span.
const overlapsDay = (evt: AgendaItem, dayStart: number, dayEndExclusive: number): boolean =>
  evt.start < dayEndExclusive && evt.end > dayStart;

const formatDayLabel = (dayStart: number, startToday: number): string =>
  dayStart === addDays(startToday, 1).getTime()
    ? t('dashboard_agenda_tomorrow', {date: format(dayStart, 'MMM d')}).toString()
    : format(dayStart, 'EEEE, MMM d');

const DayDivider = ({label}: {label: string}) => (
  <Box
    position="sticky"
    paddingBottom="2"
    marginBottom="8"
    style={{top: 0, zIndex: 1, marginInline: -8}}
  >
    <Split alignY="center" gap="0">
      <Split.Item style={{width: ACCENT_BAR_WIDTH, flexShrink: 0}} />
      <Split.Item width="fill">
        <Box backgroundColor="Background Base" paddingX="8">
          <Split alignY="center" gap="8">
            <Split.Item>
              <Text size="small" color="subtle">
                {label}
              </Text>
            </Split.Item>
            <Split.Item width="fill">
              <Box backgroundColor="Border Subtle" style={{height: 1}} />
            </Split.Item>
          </Split>
        </Box>
      </Split.Item>
    </Split>
  </Box>
);

const TIME_COL_WIDTH = 64;
const ACCENT_BAR_WIDTH = 2;

const countdownUnderTime = (evt: AgendaItem, now: number, dayStart: number): string => {
  if (
    isAgendaItemAllDayForDay(evt, dayStart) ||
    evt.end <= now ||
    isAgendaItemInProgress(evt, now)
  ) {
    return '';
  }
  if (evt.start - now < 60 * 1000) {
    return t('agenda_starting_soon');
  }
  return formatDistanceToNow(evt.start, {addSuffix: true}).replace('about ', '');
};

const MeetingJoinButton = ({joinUrl, tileId}: {joinUrl: string; tileId?: TileId}) => (
  <ButtonLink
    to={joinUrl}
    variant="outline"
    size="small"
    withIconStart={<UIIcon src={VideoLine} size="small" />}
    onClick={() => {
      if (tileId) {
        logDashboardInteraction({
          action: 'meeting_join',
          tileId,
          destination: 'external',
        });
      }
    }}
  >
    {t('dashboard_meeting_join_now')}
  </ButtonLink>
);

/** Expandable detail for an agenda row: location, link chips, Join. */
const AgendaItemDetails = ({item, now}: {item: AgendaItem; now: number}) => {
  const tileId = useDashboardTileId();
  const canJoin = canJoinAgendaItem(item, now);
  const joinUrl = resolveAgendaJoinUrl(item.joinUrl, item.description);
  const excludeUrls = [joinUrl, item.calendarUrl].filter((url): url is string => Boolean(url));
  const descriptionLinks = extractAgendaDescriptionLinks(item.description, {excludeUrls});
  const meta = formatAgendaLocation(item.location);
  const showJoin = Boolean(joinUrl && canJoin);
  const hasLinkRow = descriptionLinks.length > 0 || showJoin;
  const isLive = isAgendaItemInProgress(item, now);

  return (
    <Stack gap="6" style={{minWidth: 0}}>
      {meta && (
        <Text size="small" color="faint">
          {meta}
        </Text>
      )}
      {hasLinkRow && (
        <Split gap="4" alignY="center" style={{flexWrap: 'wrap'}}>
          {descriptionLinks.length === 1 && (
            <Split.Item key={descriptionLinks[0].url}>
              <AgendaLinkChip
                url={descriptionLinks[0].url}
                label={descriptionLinks[0].label}
                isLive={isLive}
              />
            </Split.Item>
          )}
          {descriptionLinks.length > 1 && (
            <Split.Item>
              <AgendaLinksMenuChip links={descriptionLinks} isLive={isLive} />
            </Split.Item>
          )}
          {showJoin && joinUrl && (
            <Split.Item>
              <MeetingJoinButton joinUrl={joinUrl} tileId={tileId} />
            </Split.Item>
          )}
        </Split>
      )}
    </Stack>
  );
};

const AgendaDetailsPanel = ({
  evt,
  now,
  dayStart,
}: {
  evt: AgendaItem;
  now: number;
  dayStart: number;
}) => {
  const live = isAgendaItemInProgress(evt, now);
  const countdown = countdownUnderTime(evt, now, dayStart);
  return (
    <Box paddingTop="2">
      <Split alignY="top" gap="12">
        <Split.Item>
          <Box style={{width: TIME_COL_WIDTH}}>
            {live ? (
              <NowPill />
            ) : (
              countdown && (
                <Text
                  size="xsmall"
                  color="faint"
                  style={{fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap'}}
                >
                  {countdown}
                </Text>
              )
            )}
          </Box>
        </Split.Item>
        <Split.Item width="fill" style={{minWidth: 0}}>
          <AgendaItemDetails item={evt} now={now} />
        </Split.Item>
      </Split>
    </Box>
  );
};

const NowPill = () => (
  <Box display="flex" alignItems="center" flexShrink={0} style={{gap: 2, marginLeft: -6}}>
    <UIIcon
      src={CircleSmallFilledFill}
      size="small"
      className={[styles.liveDot, atoms({color: 'Alert Base'})].join(' ')}
    />
    <Text size="xsmall" isBold style={{fontSize: 11, lineHeight: '14px'}}>
      {t('agenda_now')}
    </Text>
  </Box>
);

const RowContent = ({
  evt,
  kind,
  allDay,
  expandable = false,
  expanded = false,
}: {
  evt: AgendaItem;
  kind: RowKind;
  allDay: boolean;
  expandable?: boolean;
  expanded?: boolean;
}) => {
  const isPast = kind === 'past';
  const muted = kind !== 'highlight';
  const timeColor = isPast ? 'faint' : 'subtle';
  const titleColor = isPast ? 'faint' : kind === 'highlight' ? 'standard' : 'subtle';

  return (
    <Split alignY="center" gap="12">
      <Split.Item>
        <Box style={{width: TIME_COL_WIDTH}}>
          <Text size="small" color={timeColor} style={{fontVariantNumeric: 'tabular-nums'}}>
            {allDay ? t('all_day') : format(evt.start, 'h:mm a')}
          </Text>
        </Box>
      </Split.Item>
      <Split.Item width="fill" style={{minWidth: 0}}>
        <Text isBold={!muted} color={titleColor} tagName="div" style={{minWidth: 0}}>
          <Truncate observeParent tooltipControlProps={{placement: 'top'}}>
            {evt.title}
          </Truncate>
        </Text>
      </Split.Item>
      {expandable && (
        <Split.Item>
          <UIIcon
            src={expanded ? ChevronDownLine : ChevronRightLine}
            size="small"
            className={[expanded ? '' : styles.rowChevron, atoms({color: 'Text Subtle'})]
              .filter(Boolean)
              .join(' ')}
          />
        </Split.Item>
      )}
    </Split>
  );
};

type SkeletonTextWidth = ComponentProps<typeof Skeleton.Text>['width'];

const SKELETON_ROW_ACCENT: ComponentProps<typeof Box>['borderColor'] = 'Border Subtle';

const AgendaRowSkeleton = ({titleWidth}: {titleWidth: SkeletonTextWidth}) => (
  <Box
    borderLeft="Solid"
    borderColor={SKELETON_ROW_ACCENT}
    borderWidth="2"
    borderRadius="Medium"
    borderTopLeftRadius="None"
    borderBottomLeftRadius="None"
    paddingX="8"
    paddingY="6"
    style={{marginInline: -8}}
  >
    <Split alignY="center" gap="12">
      <Split.Item>
        <Box style={{width: TIME_COL_WIDTH}}>
          <Skeleton.Text size="small" width={44} />
        </Box>
      </Split.Item>
      <Split.Item width="fill" style={{minWidth: 0}}>
        <Skeleton.Text size="small" width={titleWidth} />
      </Split.Item>
    </Split>
  </Box>
);

const AgendaHighlightSkeleton = () => (
  <Box
    borderLeft="Solid"
    borderColor={SKELETON_ROW_ACCENT}
    borderWidth="2"
    borderTopRightRadius="Medium"
    borderBottomRightRadius="Medium"
    paddingX="8"
    paddingY="8"
    marginY="4"
    style={{marginInline: -8}}
  >
    <Stack gap="6" style={{minWidth: 0}}>
      <Split alignY="center" gap="12">
        <Split.Item>
          <Box style={{width: TIME_COL_WIDTH}}>
            <Skeleton.Text size="small" width={44} />
          </Box>
        </Split.Item>
        <Split.Item width="fill" style={{minWidth: 0}}>
          <Skeleton.Text width={168} />
        </Split.Item>
      </Split>
      <Split alignY="top" gap="12">
        <Split.Item>
          <Box style={{width: TIME_COL_WIDTH}}>
            <Skeleton.Text size="xsmall" width={36} />
          </Box>
        </Split.Item>
        <Split.Item width="fill" style={{minWidth: 0}}>
          <Skeleton.Avatar size="small" />
        </Split.Item>
      </Split>
    </Stack>
  </Box>
);

const AgendaSkeleton = () => (
  <Tile tileId="agenda">
    <Box style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          marginInline: -8,
          paddingInline: 8,
        }}
      >
        <AgendaHighlightSkeleton />
        <AgendaRowSkeleton titleWidth={140} />
      </Box>
    </Box>
  </Tile>
);

const AgendaPersonalCalendarHint = () => (
  <Text size="small" color="faint">
    <Trans
      i18nKey="agenda_empty_hint"
      t={t}
      components={{
        Link: <Link monochromatic to="https://www.dropbox.com/apps/google_deep_integration" />,
      }}
    />
  </Text>
);

export const AgendaTile = ({state}: {state: DashboardState}) => {
  const bodyRef = useSkeletonFadeIn(state.agendaLoading);
  if (state.agendaLoading) {
    return <AgendaSkeleton />;
  }

  return <AgendaTileContent state={state} bodyRef={bodyRef} />;
};

const AgendaTileContent = ({
  state,
  bodyRef,
}: {
  state: DashboardState;
  bodyRef: RefObject<HTMLDivElement>;
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [spacerHeight, setSpacerHeight] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const firstUpcomingDayRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);
  const programmaticScrollRef = useRef(false);

  const now = state.now;

  const sorted = useMemo(() => [...state.agenda].sort((a, b) => a.start - b.start), [state.agenda]);

  const {startToday, startTomorrow} = useMemo(() => {
    const todayMidnight = startOfDay(now).getTime();
    return {startToday: todayMidnight, startTomorrow: addDays(todayMidnight, 1).getTime()};
  }, [now]);

  const todayItems = useMemo(
    () => sorted.filter((evt) => overlapsDay(evt, startToday, startTomorrow)),
    [sorted, startToday, startTomorrow]
  );

  // Future days: only events that start that day (avoids repeating multi-day items).
  const upcomingDays = useMemo(() => {
    const groups: {dayStart: number; items: AgendaItem[]}[] = [];
    for (let offset = 1; offset <= UPCOMING_DAYS; offset++) {
      const dayStart = addDays(startToday, offset).getTime();
      const dayEnd = addDays(startToday, offset + 1).getTime();
      const items = sorted.filter((evt) => evt.start >= dayStart && evt.start < dayEnd);
      if (items.length > 0) {
        groups.push({dayStart, items});
      }
    }
    return groups;
  }, [sorted, startToday]);

  const todayAllDay = useMemo(
    () => todayItems.filter((evt) => isAgendaItemAllDayForDay(evt, startToday)),
    [todayItems, startToday]
  );
  const todayTimed = useMemo(
    () => todayItems.filter((evt) => !isAgendaItemAllDayForDay(evt, startToday)),
    [todayItems, startToday]
  );

  const rows = useMemo(() => {
    const hasLive = todayTimed.some((evt) => evt.end > now && isAgendaItemInProgress(evt, now));
    // When something is live, only in-progress rows are highlighted. Otherwise
    // the first upcoming row keeps the existing single-focus treatment.
    let nextHighlightAssigned = hasLive;
    return todayTimed.map((evt): {evt: AgendaItem; kind: RowKind} => {
      if (evt.end <= now) {
        return {evt, kind: 'past'};
      }
      if (isAgendaItemInProgress(evt, now)) {
        return {evt, kind: 'highlight'};
      }
      if (!nextHighlightAssigned) {
        nextHighlightAssigned = true;
        return {evt, kind: 'highlight'};
      }
      return {evt, kind: 'upcoming'};
    });
  }, [todayTimed, now]);

  const highlightId = rows.find((row) => row.kind === 'highlight')?.evt.id ?? null;
  const firstUpcomingDayStart = upcomingDays[0]?.dayStart ?? null;
  const scrollToUpcomingDay = !highlightId && upcomingDays.length > 0;

  useEffect(() => {
    userScrolledRef.current = false;
  }, [highlightId, firstUpcomingDayStart]);

  const handleScroll = () => {
    if (programmaticScrollRef.current) {
      programmaticScrollRef.current = false;
      return;
    }
    userScrolledRef.current = true;
  };

  const toggleExpanded = (id: string) => {
    userScrolledRef.current = true;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      const willExpand = !next.has(id);
      if (willExpand) {
        next.add(id);
      } else {
        next.delete(id);
      }
      logDashboardInteraction({
        action: willExpand ? 'agenda_expand' : 'agenda_collapse',
        tileId: 'agenda',
        eventId: id === ALLDAY_GROUP_KEY ? 'allday_group' : id,
      });
      return next;
    });
  };

  const expandableRowProps = (id: string, expanded: boolean) => ({
    className: styles.expandableRow,
    role: 'button' as const,
    tabIndex: 0,
    'aria-expanded': expanded,
    onClick: () => toggleExpanded(id),
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleExpanded(id);
      }
    },
    style: {cursor: 'pointer'} as const,
  });

  const renderEventRow = (evt: AgendaItem, kind: RowKind, dayStart = startToday) => {
    const highlighted = kind === 'highlight';
    const expandable = !highlighted && hasAgendaExpandableContent(evt);
    const expanded = highlighted || (expandable && expandedIds.has(evt.id));
    const live = highlighted && isAgendaItemInProgress(evt, now);
    const allDay = isAgendaItemAllDayForDay(evt, dayStart);
    const rowContent = (
      <Stack
        gap="2"
        paddingBottom={expanded && !highlighted ? '8' : undefined}
        style={{minWidth: 0}}
      >
        <RowContent
          evt={evt}
          kind={kind}
          allDay={allDay}
          expandable={expandable}
          expanded={expanded}
        />
        {expanded && <AgendaDetailsPanel evt={evt} now={now} dayStart={dayStart} />}
      </Stack>
    );

    if (highlighted) {
      return (
        <Box
          key={evt.id}
          ref={evt.id === highlightId ? highlightRef : undefined}
          backgroundColor={live ? 'Primary Surface' : undefined}
          borderLeft="Solid"
          borderColor={live ? 'Primary Base - State 2' : 'Primary Base'}
          borderTopRightRadius="Medium"
          borderBottomRightRadius="Medium"
          borderWidth="2"
          paddingX="8"
          paddingY="8"
          marginBottom="2"
          style={{marginInline: -8}}
        >
          {rowContent}
        </Box>
      );
    }

    return (
      <Box key={evt.id}>
        {expandable ? (
          <Box {...expandableRowProps(evt.id, expanded)}>
            <TileRow interactive accent={getRowAccent(evt, kind)} faded={kind === 'past'}>
              {rowContent}
            </TileRow>
          </Box>
        ) : (
          <TileRow accent={getRowAccent(evt, kind)} faded={kind === 'past'}>
            <RowContent evt={evt} kind={kind} allDay={allDay} />
          </TileRow>
        )}
      </Box>
    );
  };

  const renderAllDaySection = () => {
    if (todayAllDay.length === 0) {
      return null;
    }
    if (todayAllDay.length === 1) {
      return renderEventRow(todayAllDay[0], 'allday');
    }
    const expanded = expandedIds.has(ALLDAY_GROUP_KEY);
    return (
      <Box>
        <Box {...expandableRowProps(ALLDAY_GROUP_KEY, expanded)}>
          <TileRow interactive>
            <Split alignY="center" gap="12">
              <Split.Item>
                <Box style={{width: TIME_COL_WIDTH}}>
                  <Text size="small" color="subtle">
                    {t('all_day')}
                  </Text>
                </Box>
              </Split.Item>
              <Split.Item width="fill" style={{minWidth: 0}}>
                <Text color="subtle" tagName="div" style={{minWidth: 0}}>
                  <Truncate observeParent>
                    {t('dashboard_agenda_allday_count', {count: todayAllDay.length})}
                  </Truncate>
                </Text>
              </Split.Item>
              <Split.Item>
                <UIIcon
                  src={expanded ? ChevronDownLine : ChevronRightLine}
                  size="small"
                  className={[expanded ? '' : styles.rowChevron, atoms({color: 'Text Subtle'})]
                    .filter(Boolean)
                    .join(' ')}
                />
              </Split.Item>
            </Split>
          </TileRow>
        </Box>
        {expanded && todayAllDay.map((evt) => renderEventRow(evt, 'allday'))}
      </Box>
    );
  };

  // Auto-scroll anchor to top until the user scrolls or expands a row.
  useEffect(() => {
    const list = listRef.current;
    const anchor =
      highlightRef.current ?? (scrollToUpcomingDay ? firstUpcomingDayRef.current : null);
    if (!list || !anchor) {
      if (spacerHeight !== 0) {
        setSpacerHeight(0);
      }
      return;
    }
    const anchorTop =
      anchor.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
    const contentBelowAnchor = list.scrollHeight - spacerHeight - anchorTop;
    const neededSpacer = Math.max(0, list.clientHeight - contentBelowAnchor - 4);
    if (Math.abs(neededSpacer - spacerHeight) > 1) {
      setSpacerHeight(neededSpacer);
      return;
    }
    if (userScrolledRef.current) {
      return;
    }
    const target = Math.max(0, anchorTop - 4);
    if (Math.abs(list.scrollTop - target) > 1) {
      programmaticScrollRef.current = true;
      list.scrollTop = target;
    }
  }, [highlightId, firstUpcomingDayStart, scrollToUpcomingDay, expandedIds, spacerHeight]);

  const isEmpty = todayTimed.length === 0 && todayAllDay.length === 0 && upcomingDays.length === 0;
  const hasPersonalEvents = sorted.some((evt) => evt.source !== 'company');
  const showCalendarHint = !hasPersonalEvents;

  return (
    <Tile tileId="agenda" bodyRef={bodyRef}>
      <Box style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        {isEmpty ? (
          <Stack gap="4">
            <Text color="faint">{t('agenda_empty')}</Text>
            {showCalendarHint && <AgendaPersonalCalendarHint />}
          </Stack>
        ) : (
          <Box
            ref={listRef}
            onScroll={handleScroll}
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              marginInline: -8,
              paddingInline: 8,
            }}
          >
            {renderAllDaySection()}
            {rows.map(({evt, kind}) => renderEventRow(evt, kind))}
            {upcomingDays.map((group, index) => (
              <Box
                key={group.dayStart}
                ref={index === 0 ? firstUpcomingDayRef : undefined}
                marginTop="24"
              >
                <DayDivider label={formatDayLabel(group.dayStart, startToday)} />
                {group.items.map((evt) => renderEventRow(evt, 'future', group.dayStart))}
              </Box>
            ))}
            {showCalendarHint && (
              <Box marginTop="36" paddingBottom="4">
                <AgendaPersonalCalendarHint />
              </Box>
            )}
            {spacerHeight > 0 && <div aria-hidden style={{height: spacerHeight, flexShrink: 0}} />}
          </Box>
        )}
      </Box>
    </Tile>
  );
};
